/**
 * Matter Manager Page
 * Admin page for managing Echo matters with billing rates
 */

import React, { useCallback } from 'react';
import { Card, Stack, useTheme, spacing, textStyles, fontSize } from '@fti/design-system';
import { MatterManager } from '../components/echo/MatterManager';
import { useEchoMatters, useMatters, useEchoMatterMutations } from '../api/echo';

/**
 * Admin page for managing Echo matters
 */
export function MatterManagerPage() {
  const { theme } = useTheme();
  const { data: echoMatters, loading: isLoadingEcho, refetch: refetchEchoMatters } = useEchoMatters();
  const { data: allMatters, loading: isLoadingMatters } = useMatters();
  const { createEchoMatter, updateEchoMatter } = useEchoMatterMutations();

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;

  const handleAddMatter = useCallback(async (matterId: number, billingRate: number) => {
    // Find the matter to get its details
    const matter = allMatters?.find((m) => m.id === matterId);
    if (!matter) {
      throw new Error('Matter not found');
    }

    await createEchoMatter({
      matter_id: matter.id,
      billing_rate: billingRate,
    });

    // Refresh the list
    refetchEchoMatters();
  }, [allMatters, createEchoMatter, refetchEchoMatters]);

  const handleUpdateBillingRate = useCallback(async (echoMatterId: number, newRate: number) => {
    await updateEchoMatter(echoMatterId, { billing_rate: newRate });
    refetchEchoMatters();
  }, [updateEchoMatter, refetchEchoMatters]);

  return (
    <Stack gap="lg">
      {/* Page Header */}
      <div>
        <h1 style={{ margin: 0, ...textStyles.h3, color: textColor }}>
          Matter Code Manager
        </h1>
        <p style={{ margin: 0, marginTop: spacing.xs, ...textStyles.body, color: mutedColor }}>
          Add and manage matter codes for Echo billing
        </p>
      </div>

      {/* Matter Manager */}
      <MatterManager
        availableMatters={allMatters || []}
        echoMatters={echoMatters || []}
        isLoadingAvailable={isLoadingMatters}
        isLoadingEcho={isLoadingEcho}
        onAddMatter={handleAddMatter}
        onUpdateBillingRate={handleUpdateBillingRate}
      />

      {/* Help Section */}
      <Card variant="outlined">
        <Card.Body>
          <Stack gap="sm">
            <h3 style={{ margin: 0, ...textStyles.label, color: textColor }}>
              About Matter Management
            </h3>
            <ul
              style={{
                margin: 0,
                paddingLeft: spacing.lg,
                color: mutedColor,
                fontSize: fontSize.sm,
              }}
            >
              <li>Matters must be added to Echo before they can be used for workflows</li>
              <li>Each matter has a billing rate that determines cost calculations</li>
              <li>Billing rates can be updated at any time for future workflows</li>
              <li>Existing workflows retain their original billing rate</li>
            </ul>
          </Stack>
        </Card.Body>
      </Card>
    </Stack>
  );
}

export default MatterManagerPage;
